/**
 * LLMEvent Stream Processor
 * Manages streaming data processing and state updates
 * Uses stateless message processor for event transformation
 */

import type {
  LLMEvent,
  ThreadMessage,
  MessageContent,
  ToolCallContent,
  SubSessionContent,
} from '@/types/messages';
import { EventType } from '@/types/messages';
import {
  processLLMEvent,
  generateMessageId,
  type ProcessingContext,
} from './StatelessMessageProcessor';
import { logger } from '@/utils/logger';
import { STREAM_TIMEOUT_ERROR_SOURCE } from '@/constants/ui.constants';
import { exchangeEncryptionKey } from '@/api/encryption.api';
import { decryptResponsePayload } from '@/helpers/encryption.helpers';
import type { EncryptionKey } from '@/interfaces/encryption.interface';
import type { AxiosResponse } from 'axios';
import type { EncryptorType } from '@/interfaces/systemConfig.interface';

export interface MessageStateManager {
  getMessage: (messageId: string) => ThreadMessage | null;
  addMessage: (message: ThreadMessage) => void;
  updateMessage: (
    messageId: string,
    updater: (message: ThreadMessage) => ThreadMessage
  ) => ThreadMessage | null;
  getThreadMessages: (threadId: string) => ThreadMessage[];
}

interface ThreadStreamState {
  eventOrderCounter: number;
  buffer: string;
  turnTimestamp: number | null;
  processedToolCallIds: Set<string>;
  receivedFinished: boolean;
  /** DB UUID override for reconnect replays; null means use generated ID. */
  messageIdOverride: string | null;
  /**
   * When true, the next text chunk will strip cached top-level text blocks
   * before appending, preventing duplication during a full 0-0 replay.
   */
  replaceTextOnNextChunk: boolean;
}

function createDefaultThreadState(): ThreadStreamState {
  return {
    eventOrderCounter: 0,
    buffer: '',
    turnTimestamp: null,
    processedToolCallIds: new Set(),
    receivedFinished: false,
    messageIdOverride: null,
    replaceTextOnNextChunk: false,
  };
}

export class LLMEventStreamProcessor {
  private static readonly SSE_EVENT_PREFIX_LENGTH = 7; // "event: "
  private static readonly SSE_DATA_PREFIX_LENGTH = 6; // "data: "

  private messageStateManager: MessageStateManager;
  private subscribers = new Set<(messages: ThreadMessage[]) => void>();
  private threadState = new Map<string, ThreadStreamState>();

  constructor(messageStateManager: MessageStateManager) {
    this.messageStateManager = messageStateManager;
  }

  private getThreadState(threadId: string): ThreadStreamState {
    let state = this.threadState.get(threadId);
    if (!state) {
      state = createDefaultThreadState();
      this.threadState.set(threadId, state);
    }
    return state;
  }

  private getStreamBuffer(threadId: string): string {
    return this.getThreadState(threadId).buffer;
  }

  private setStreamBuffer(threadId: string, value: string): void {
    this.getThreadState(threadId).buffer = value;
  }

  private incrementEventOrder(threadId: string): number {
    const state = this.getThreadState(threadId);
    state.eventOrderCounter += 1;
    return state.eventOrderCounter;
  }

  private getCurrentTurnTimestamp(threadId: string): number | null {
    return this.getThreadState(threadId).turnTimestamp;
  }

  private setCurrentTurnTimestamp(threadId: string, value: number | null): void {
    this.getThreadState(threadId).turnTimestamp = value;
  }

  /** Lets callers distinguish a clean completion from a transport drop. */
  public hasReceivedFinished(threadId: string): boolean {
    return this.getThreadState(threadId).receivedFinished;
  }

  /**
   * Override the message ID used for the primary turn message during
   * stream reconnect.  Pass the DB UUID of the existing streaming message
   * so that replayed events update it instead of creating a duplicate.
   * Pass null to clear the override.  When id is non-null, pass threadId
   * so the override only applies to chunks from that thread (prevents
   * parallel streams from misrouting).
   *
   * @param id — DB UUID to use as the message ID for replayed events,
   *   or null to clear the override.
   * @param replaceText — when true (default for a full 0-0 replay),
   *   the first replayed text chunk atomically strips cached text so
   *   the content isn't duplicated.  Pass false when resuming from a
   *   saved Valkey position: in that case the cached text stays intact
   *   and only NEW events are appended.
   * @param threadId — when id is non-null, the thread this override
   *   applies to.  Only chunks with matching threadId will use it.
   */
  public setMessageIdOverride(
    id: string | null,
    replaceText: boolean = true,
    threadId?: string,
  ): void {
    if (id === null) {
      if (threadId !== undefined) {
        const state = this.getThreadState(threadId);
        state.messageIdOverride = null;
        state.replaceTextOnNextChunk = false;
      } else {
        this.threadState.forEach((state) => {
          state.messageIdOverride = null;
          state.replaceTextOnNextChunk = false;
        });
      }
    } else if (threadId !== undefined) {
      const state = this.getThreadState(threadId);
      state.messageIdOverride = id;
      state.replaceTextOnNextChunk = replaceText;
    }
  }

  /**
   * Start a live turn as an empty streaming message under the backend
   * message id, the shape a reconnect resumes, and route the thread's
   * events to it.
   */
  public startBackendTurn(threadId: string, messageId: string): void {
    const message: ThreadMessage = {
      id: messageId,
      threadId,
      role: 'assistant',
      content: [],
      status: 'streaming',
      createdAt: new Date(),
      updatedAt: new Date(),
      metadata: {},
    };
    this.messageStateManager.addMessage(message);
    this.notifySubscribers([message]);
    this.setMessageIdOverride(messageId, false, threadId);
  }

  /**
   * Restore cached content to an existing message.
   *
   * Unlike calling setMessages() directly (which only updates React state
   * asynchronously), this method also updates messagesRef.current
   * synchronously so that the very next processStreamChunk call can find
   * the restored message via getMessage() and use updateMessage() rather
   * than addMessage().  Without this, if a reconnect event arrives before
   * React has re-rendered the restored state, addMessage() would replace
   * the restored content with just the first replayed event's data.
   */
  public setMessageContent(
    messageId: string,
    content: ThreadMessage['content'],
  ): void {
    this.messageStateManager.updateMessage(messageId, (msg) => ({
      ...msg,
      content,
    }));
  }

  /**
   * Apply backend state from Redis to the stream processor.
   *
   * This method is called during reconnection to restore the message
   * state from the backend's Redis store. It:
   * 1. Updates the message content with the backend's accumulated state
   * 2. Merges the backend's accumulated metadata (subgraph,
   *    availableIds, tokenUsage, …) into the message so artefacts
   *    produced *before* the reconnect window survive the swap.
   * 3. Seeds the eventOrderCounter from the backend's eventOrder
   * 4. Populates processedToolCallIds to prevent duplicate tool calls
   *
   * After calling this, use setMessageIdOverride(messageId, false) and
   * connect to the SSE stream with the lastEventId from the backend state.
   */
  public applyBackendState(
    messageId: string,
    content: ThreadMessage['content'],
    eventOrder: number,
    metadata?: Record<string, unknown>,
  ): void {
    // Update message content + metadata atomically.  The backend's
    // FINISHED handler aggregates `subgraph` and `availableIds` into
    // state.metadata; without this merge a Reconnect swap would drop
    // them (the message starts with metadata={}) and the user would
    // have to refresh the browser to see them again.
    this.messageStateManager.updateMessage(messageId, (msg) => ({
      ...msg,
      content,
      metadata: metadata
        ? { ...msg.metadata, ...metadata }
        : msg.metadata,
    }));

    // Seed eventOrderCounter and processedToolCallIds from backend state
    const message = this.messageStateManager.getMessage(messageId);
    if (message?.threadId) {
      const state = this.getThreadState(message.threadId);
      state.eventOrderCounter = eventOrder;

      // Populate processedToolCallIds from the content to prevent duplicates
      const toolCallIds = new Set<string>();
      this.extractToolCallIds(content, toolCallIds);
      if (toolCallIds.size > 0) {
        state.processedToolCallIds = toolCallIds;
        logger.debug(
          `[applyBackendState] Populated ${toolCallIds.size} tool call IDs ` +
            `for thread ${message.threadId}`
        );
      }
    }

    logger.info(
      `[applyBackendState] Applied backend state to message ${messageId}: ` +
        `${content.length} blocks, eventOrder=${eventOrder}, ` +
        `metadataKeys=[${
          metadata ? Object.keys(metadata).join(',') : ''
        }]`
    );
  }

  /**
   * Recursively extract tool call IDs from content blocks.
   */
  private extractToolCallIds(
    content: ThreadMessage['content'],
    ids: Set<string>,
  ): void {
    for (const block of content) {
      // Check for tool-call blocks
      if (block.type === 'tool-call') {
        const toolCallBlock = block as ToolCallContent;
        if (toolCallBlock.toolCallId) {
          ids.add(toolCallBlock.toolCallId);
        }
      }
      // Check sub-sessions for nested tool calls
      if (block.type === 'sub-session') {
        const subSession = block as SubSessionContent;
        if (Array.isArray(subSession.content)) {
          this.extractToolCallIds(subSession.content, ids);
        }
      }
    }
  }


  /**
   * Clear the content of an existing message before a stream replay.
   *
   * When the Valkey stream is replayed from lastEventId=0-0 the events
   * reconstruct the full message from scratch.  If the DB-loaded message
   * already has partial content stored, those chunks would be duplicated
   * by the replay.  Clearing the content beforehand (via the
   * messageStateManager so messagesRef is updated synchronously) lets
   * the replay rebuild the content cleanly.
   */
  public clearMessageContent(messageId: string): void {
    this.messageStateManager.updateMessage(messageId, (msg) => ({
      ...msg,
      content: [],
    }));
  }

  /**
   * Process a stream chunk and notify subscribers
   */
  public processStreamChunk(
    chunk: string,
    threadId: string,
    timestamp?: number
  ): void {
    // Add chunk to stream buffer (per-thread to avoid parallel stream mixing)
    const currentBuffer = this.getStreamBuffer(threadId) + chunk;
    this.setStreamBuffer(threadId, currentBuffer);

    // Split by double newlines to get complete SSE events (simple approach)
    const events = currentBuffer.split('\n\n');
    const incomplete = events.pop() || '';
    this.setStreamBuffer(threadId, incomplete);

    // Process each complete SSE event
    for (const eventBlock of events) {
      if (eventBlock.trim().length === 0) continue;

      const event = this.parseSSEEvent(eventBlock);
      if (!event) {
        continue;
      }

      // One connection's deadline, not the message's end: the caller
      // reconnects and the turn goes on.
      if (
        event.type === EventType.ERROR &&
        event.metadata?.error_source === STREAM_TIMEOUT_ERROR_SOURCE
      ) {
        continue;
      }

      // Increment event order for each event (per-thread)
      const eventOrder = this.incrementEventOrder(threadId);

      // Use consistent timestamp for current conversation turn (per-thread)
      let turnTimestamp = this.getCurrentTurnTimestamp(threadId);
      if (!turnTimestamp) {
        turnTimestamp = timestamp || Date.now();
        this.setCurrentTurnTimestamp(threadId, turnTimestamp);
      }

      // Use session_id consistently for message grouping
      const effectiveSessionId = event.metadata?.session_id;

      const context: ProcessingContext = {
        threadId,
        timestamp: turnTimestamp,
        eventOrder,
        sessionId: effectiveSessionId,
      };

      // Generate message ID using consistent timestamp for the turn.
      // When a messageIdOverride is set for this thread (stream reconnect),
      // use that UUID so replayed events update the existing message.
      // Override is scoped by threadId so parallel streams do not misroute.
      const threadStreamState = this.getThreadState(threadId);
      const messageId =
        threadStreamState.messageIdOverride ??
        generateMessageId(threadId, turnTimestamp, effectiveSessionId);

      // Handle AI_MESSAGE_CHUNK events inline (no buffering)
      if (event.type === EventType.AI_MESSAGE_CHUNK) {
        const shouldReplaceText =
          threadStreamState.replaceTextOnNextChunk &&
          threadStreamState.messageIdOverride === messageId &&
          this.messageStateManager.getMessage(messageId) !== null;

        if (shouldReplaceText) {
          threadStreamState.replaceTextOnNextChunk = false;
        }

        const updatedMessage = this.messageStateManager.updateMessage(
          messageId,
          (latestMessage) => {
            let base = latestMessage;
            if (shouldReplaceText && base) {
              base = {
                ...base,
                content: base.content.filter((b) => b.type !== 'text'),
              };
            }
            return (
              processLLMEvent(event, context, base) || base || latestMessage!
            );
          }
        );

        if (!updatedMessage) {
          const newMsg = processLLMEvent(event, context, null);
          if (newMsg) {
            this.messageStateManager.addMessage(newMsg);
            this.notifySubscribers([newMsg]);
          }
        } else {
          this.notifySubscribers([updatedMessage]);
        }
        continue;
      }

      // Handle THINKING and THINKING_CHUNK events inline – same high-frequency
      // fast path as AI_MESSAGE_CHUNK so thinking tokens stream smoothly.
      if (
        event.type === EventType.THINKING ||
        event.type === EventType.THINKING_CHUNK
      ) {
        const thinkingUpdated = this.messageStateManager.updateMessage(
          messageId,
          (latestMessage) =>
            processLLMEvent(event, context, latestMessage) ||
            latestMessage!
        );

        if (!thinkingUpdated) {
          const newMsg = processLLMEvent(event, context, null);
          if (newMsg) {
            this.messageStateManager.addMessage(newMsg);
            this.notifySubscribers([newMsg]);
          }
        } else {
          this.notifySubscribers([thinkingUpdated]);
        }
        continue;
      }

      // Process other events normally
      const currentMessage = this.messageStateManager.getMessage(messageId);

      // Skip duplicate tool calls - use O(1) Set lookup instead of O(n) search
      if (
        (event.type === EventType.TOOL_CALL ||
          event.type === EventType.TOOL_CALL_START) &&
        event.tool_call_id
      ) {
        const { processedToolCallIds } = this.getThreadState(threadId);
        if (processedToolCallIds.has(event.tool_call_id)) {
          logger.debug(
            `[StreamProcessor] Skipping duplicate tool call: ${event.tool_call_id}`
          );
          continue;
        }
        processedToolCallIds.add(event.tool_call_id);
      }

      // Special handling for TOOL_RESULT events - they need to find their
      // matching tool call
      if (event.type === EventType.TOOL_RESULT && event.tool_call_id) {
        if (this.handleToolResultEvent(event, context, messageId, threadId)) {
          continue; // Event handled, move to next
        }
      }

      // CRITICAL: Use updater function pattern to ensure atomic updates with
      // latest state. This prevents race conditions when multiple events
      // arrive in quick succession
      let updatedMessage: ThreadMessage | null = null;

      // For new messages, process immediately and add
      if (!currentMessage) {
        updatedMessage = processLLMEvent(event, context, null);
        if (updatedMessage) {
          logger.info(
            `[StreamProcessor] Adding new message: id=${updatedMessage.id}, `
              + `type=${event.type}, role=${updatedMessage.role}`
          );
          this.messageStateManager.addMessage(updatedMessage);
          this.notifySubscribers([updatedMessage]);
          logger.info(
            `[StreamProcessor] Message added and subscribers notified: `
              + `id=${updatedMessage.id}`
          );
        } else {
          logger.warn(
            `[StreamProcessor] processLLMEvent returned null for new message, event: ${event.type}`
          );
          continue;
        }
      } else {
        // For existing messages, use updater function to process with latest
        // state atomically. This ensures we don't lose updates from
        // concurrent events
        updatedMessage = this.messageStateManager.updateMessage(
          messageId,
          (latestMessage) => {
            return (
              processLLMEvent(event, context, latestMessage) || latestMessage
            );
          }
        );

        if (!updatedMessage) {
          logger.warn(
            `[StreamProcessor] updateMessage returned null for event: ${event.type}`
          );
          continue;
        }

        this.notifySubscribers([updatedMessage]);
      }

      // Get the final updated message for verification (after update or add)
      const finalMessage = this.messageStateManager.getMessage(messageId);

      // Special handling for DATA events (like token_usage) - find latest
      // assistant message
      if (
        !finalMessage &&
        event.type === EventType.DATA &&
        event.id === 'token_usage'
      ) {
        if (this.handleDataEvent(event, context, threadId)) {
          continue; // Event handled, move to next
        }
      }

      // Special handling for FINISHED events when no existing message is found
      if (!finalMessage && event.type === EventType.FINISHED) {
        this.getThreadState(threadId).receivedFinished = true;
        if (this.handleFinishedEvent(event, context, threadId)) {
          continue; // Event handled, move to next
        }
      }

      // ERROR is a terminal event.
      if (!finalMessage && event.type === EventType.ERROR) {
        this.getThreadState(threadId).receivedFinished = true;
        logger.debug(
          '[FINISH_STATE] ERROR event with no message, marking as terminal'
        );
      }

      if (finalMessage) {
        if (event.type === EventType.FINISHED) {
          this.getThreadState(threadId).receivedFinished = true;
          logger.debug(
            '[FINISH_STATE] FINISHED event metadata:',
            event.metadata
          );
          if (event.metadata?.availableIds) {
            logger.debug(
              '[FINISH_STATE] availableIds present in FINISHED event:',
              event.metadata.availableIds.length
            );
          }
        }

        if (event.type === EventType.ERROR) {
          this.getThreadState(threadId).receivedFinished = true;
          logger.debug('[FINISH_STATE] ERROR event received, marking as terminal');
        }

        this.verifyToolCallStored(event, finalMessage);
      }
    }
  }

  /**
   * Handle TOOL_RESULT events - find matching tool call and update
   */
  private handleToolResultEvent(
    event: LLMEvent,
    context: ProcessingContext,
    messageId: string,
    threadId: string
  ): boolean {
    if (!event.tool_call_id) {
      return false;
    }

    // First check if messageId points to a message with the tool call
    let messageToUpdate: ThreadMessage | null = null;
    const messageById = this.messageStateManager.getMessage(messageId);

    if (
      messageById &&
      this.messageContainsToolCall(messageById, event.tool_call_id)
    ) {
      messageToUpdate = messageById;
    } else {
      // Search for the tool call across all messages in the thread
      const allMessages =
        this.messageStateManager.getThreadMessages(threadId);
      for (const msg of allMessages) {
        logger.debug(
          `[StreamProcessor] Checking message ${msg.id} with ${msg.content.length} content items`
        );

        if (this.messageContainsToolCall(msg, event.tool_call_id)) {
          messageToUpdate = msg;
          break;
        }
      }
    }

    if (messageToUpdate) {
      // Use updater function to atomically update with latest state
      const resultMessage = this.messageStateManager.updateMessage(
        messageToUpdate.id,
        (latestMessage) => {
          return (
            processLLMEvent(event, context, latestMessage) || latestMessage
          );
        }
      );

      if (resultMessage) {
        this.notifySubscribers([resultMessage]);
        logger.debug(
          `[StreamProcessor] Successfully updated message ${messageToUpdate.id} with tool result`
        );
        return true;
      } else {
        logger.warn(
          `[StreamProcessor] updateMessage returned null for tool result ${event.tool_call_id}`
        );
      }
    } else {
      logger.warn(
        `[StreamProcessor] No matching tool call found for: ${event.tool_call_id}`
      );
      // Log all tool calls in all messages for debugging
      this.logAllToolCalls(threadId);
    }

    return true; // Always return true to continue processing
  }

  /**
   * Handle DATA events (like token_usage) - find latest assistant message
   */
  private handleDataEvent(
    event: LLMEvent,
    context: ProcessingContext,
    threadId: string
  ): boolean {
    if (event.id !== 'token_usage') {
      return false;
    }

    const allMessages = this.messageStateManager.getThreadMessages(threadId);
    // Find the latest assistant message (streaming or complete)
    const latestAssistantMessage = allMessages
      .filter((msg) => msg.role === 'assistant')
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];

    if (latestAssistantMessage) {
      const updatedWithTokenUsage = processLLMEvent(
        event,
        context,
        latestAssistantMessage
      );
      if (updatedWithTokenUsage) {
        this.messageStateManager.updateMessage(
          latestAssistantMessage.id,
          () => updatedWithTokenUsage
        );
        this.notifySubscribers([updatedWithTokenUsage]);
        return true;
      }
    }

    return false;
  }

  /**
   * Handle FINISHED events - complete streaming messages
   */
  private handleFinishedEvent(
    event: LLMEvent,
    context: ProcessingContext,
    threadId: string
  ): boolean {
    logger.debug(
      '[FINISH_STATE] Processing FINISHED event with no current message'
    );
    logger.debug('[FINISH_STATE] FINISHED event data:', event.data);

    // Find any streaming assistant message in this thread to complete
    const allMessages = this.messageStateManager.getThreadMessages(threadId);
    const streamingAssistantMessage = allMessages
      .filter(
        (msg) => msg.role === 'assistant' && msg.status === 'streaming'
      )
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];

    if (streamingAssistantMessage) {
      logger.debug(
        '[FINISH_STATE] Found streaming message to complete:',
        streamingAssistantMessage.id
      );
      const finishedMessage = processLLMEvent(
        event,
        context,
        streamingAssistantMessage
      );
      if (finishedMessage) {
        this.messageStateManager.updateMessage(
          streamingAssistantMessage.id,
          () => finishedMessage
        );
        this.notifySubscribers([finishedMessage]);
        logger.debug(
          '[FINISH_STATE] Successfully completed message with FINISHED event'
        );
        return true;
      } else {
        logger.warn(
          '[FINISH_STATE] processLLMEvent returned null for FINISHED event'
        );
      }
    } else {
      logger.warn(
        `[FINISH_STATE] No streaming assistant message found to complete`
      );
    }

    return false;
  }

  /**
   * Verify that a tool call event was persisted in the message after processing.
   * Logs an error if the tool call ID is missing from the stored message.
   */
  private verifyToolCallStored(
    event: LLMEvent,
    message: ThreadMessage
  ): void {
    if (
      (event.type !== EventType.TOOL_CALL &&
        event.type !== EventType.TOOL_CALL_START) ||
      !event.tool_call_id
    ) {
      return;
    }
    const hasToolCall = this.messageContainsToolCall(
      message,
      event.tool_call_id
    );
    if (!hasToolCall) {
      logger.error(
        `[StreamProcessor] CRITICAL: Tool call ${event.tool_call_id} ` +
          `was NOT stored in message ${message.id} after processing!`
      );
    }
  }

  /**
   * Log all tool calls in all messages for debugging
   */
  private logAllToolCalls(threadId: string): void {
    const allMessages = this.messageStateManager.getThreadMessages(threadId);
    allMessages.forEach((msg, msgIndex) => {
      logger.debug(`[StreamProcessor] Message ${msgIndex} (${msg.id}):`);
      msg.content.forEach((content, contentIndex) => {
        if (content.type === 'tool-call') {
          const toolCall = content as ToolCallContent;
          logger.debug(
            `[StreamProcessor]   Content ${contentIndex}: tool-call with toolCallId: ${toolCall.toolCallId}`
          );
        } else if (content.type === 'sub-session') {
          const subSession = content as SubSessionContent;
          logger.debug(
            `[StreamProcessor]   Content ${contentIndex}: sub-session with ${
              subSession.content?.length || 0
            } items`
          );
          subSession.content?.forEach(
            (subContent: MessageContent, subIndex: number) => {
              if (subContent.type === 'tool-call') {
                const subToolCall = subContent as ToolCallContent;
                logger.debug(
                  `[StreamProcessor]     Sub-content ${subIndex}: tool-call with toolCallId: ${subToolCall.toolCallId}`
                );
              }
            }
          );
        } else {
          logger.debug(
            `[StreamProcessor]   Content ${contentIndex}: ${content.type}`
          );
        }
      });
    });
  }

  /**
   * Parse an SSE event block into an LLMEvent
   */
  private parseSSEEvent(eventBlock: string): LLMEvent | null {
    try {
      const lines = eventBlock.split('\n');

      // SSE comment lines start with ':' (e.g. keepalive heartbeats).
      // Silently ignore them -- they are not events.
      const isComment = lines.every(
        (l) => l.length === 0 || l.startsWith(':')
      );
      if (isComment) {
        return null;
      }

      let eventType: string | null = null;
      let data: string | null = null;
      let sseId: string | null = null;

      for (const line of lines) {
        if (line.startsWith('id: ')) {
          // Valkey stream entry ID
          sseId = line.substring(4).trim();
        } else if (line.startsWith('event: ')) {
          eventType = line
            .substring(LLMEventStreamProcessor.SSE_EVENT_PREFIX_LENGTH)
            .trim();
        } else if (line.startsWith('data: ')) {
          data = line.substring(
            LLMEventStreamProcessor.SSE_DATA_PREFIX_LENGTH
          );
        }
      }

      if (!eventType || !data) {
        logger.warn('[StreamProcessor] Incomplete SSE event:', eventBlock);
        return null;
      }

      const event = this.parseEventData(eventType, data);
      if (event && sseId) {
        event.metadata = { ...event.metadata, sse_id: sseId };
      }
      return event;
    } catch (error) {
      logger.error('Error parsing SSE event:', error, 'Block:', eventBlock);
      return null;
    }
  }

  /**
   * Add tool tags to metadata for filtering
   */
  private addToolTags(
    metadata: Record<string, unknown> | null | undefined
  ): Record<string, unknown> {
    return {
      ...metadata,
      tag: 'tool', // Tag all tool calls with "tool" for filtering
      toolTag: 'tool', // Additional tag field
    };
  }

  /**
   * Parse event data based on event type
   */
  private parseEventData(eventType: string, data: string): LLMEvent | null {
    try {
      let eventData: any;
      try {
        eventData = JSON.parse(data);
      } catch (e) {
        logger.error(e);
        // Handle non-JSON data (e.g., update_state)
        return {
          id: `${eventType}_${Date.now()}`,
          type: eventType as any, // EventType enum value
          result: data,
        };
      }

      // Handle array format for DATA events
      if (Array.isArray(eventData)) {
        eventData = eventData[0];
      }

      switch (eventType) {
        case EventType.AI_MESSAGE_CHUNK:
          return {
            ...eventData,
            type: EventType.AI_MESSAGE_CHUNK,
          };
        case EventType.AI_MESSAGE:
          return {
            ...eventData,
            type: EventType.AI_MESSAGE,
          };
        case EventType.DATA:
          return {
            id: eventData.id || `data_${Date.now()}`,
            type: EventType.DATA,
            data: eventData.data || eventData,
            metadata: eventData.metadata || eventData,
          };
        case EventType.TOOL_CALL_START:
          return {
            id: eventData.id || `tool_start_${eventData.toolCallId}`,
            type: EventType.TOOL_CALL_START,
            tool_name: eventData.toolName,
            tool_call_id: eventData.toolCallId,
            metadata: this.addToolTags(eventData.metadata),
          };
        case EventType.TOOL_CALL_CHUNK:
          return {
            id: eventData.id || `tool_chunk_${eventData.toolCallId}`,
            type: EventType.TOOL_CALL_CHUNK,
            tool_call_id: eventData.toolCallId,
            result: eventData.argsTextDelta,
            metadata: this.addToolTags(eventData.metadata),
          };
        case EventType.TOOL_CALL: {
          const toolCallEvent = {
            id: eventData.id || `tool_call_${eventData.toolCallId}`,
            type: EventType.TOOL_CALL,
            tool_name: eventData.toolName,
            title: eventData.data?.title,
            // Sent for a call drawn from its start.
            artifact: eventData.data?.artifact,
            tool_call_id: eventData.toolCallId,
            tool_params: eventData.argsText,
            metadata: this.addToolTags(eventData.metadata),
          };
          logger.debug(
            `[StreamProcessor] Parsed TOOL_CALL event: toolCallId=${toolCallEvent.tool_call_id}, ` +
              `toolName=${toolCallEvent.tool_name}, eventId=${toolCallEvent.id}, ` +
              `hasMetadata=${!!toolCallEvent.metadata}, sessionId=${toolCallEvent.metadata?.session_id}, tag=${toolCallEvent.metadata?.tag}`
          );
          return toolCallEvent;
        }
        case EventType.TOOL_RESULT:
          return {
            id: eventData.id || `tool_result_${eventData.toolCallId}`,
            type: EventType.TOOL_RESULT,
            tool_call_id: eventData.toolCallId,
            tool_name: eventData.toolName,
            result: eventData.result,
            is_error: eventData.isError || false,
            tool_params: eventData.argsText,
            artifact: eventData.data?.artifact,
            metadata: this.addToolTags(eventData.metadata),
          };
        case EventType.FINISHED:
          return {
            id: eventData.id || `finish_${Date.now()}`,
            type: EventType.FINISHED,
            data: eventData.data,
            metadata: eventData.metadata || eventData,
          };
        case EventType.TURN_FINISH:
          return {
            id: eventData.id || `turn_finish_${Date.now()}`,
            type: EventType.TURN_FINISH,
            metadata: eventData.metadata || eventData,
          };
        case EventType.ERROR:
          // Keep `metadata`: its `error_source` tells a stream_timeout
          // frame from a real failure.
          return {
            id: eventData.id || `error_${Date.now()}`,
            type: EventType.ERROR,
            error: eventData.error || eventData,
            result: eventData.message || 'An error occurred',
            metadata: eventData.metadata,
          };
        case EventType.THINKING:
          return {
            id: eventData.id || `thinking_${Date.now()}`,
            type: EventType.THINKING,
            result: eventData.result,
            metadata: eventData.metadata,
          };
        case EventType.THINKING_CHUNK:
          return {
            id: eventData.id || `thinking_chunk_${Date.now()}`,
            type: EventType.THINKING_CHUNK,
            result: eventData.result,
            metadata: eventData.metadata,
          };
        default:
          logger.warn(`[StreamProcessor] Unknown event type: ${eventType}`);
          return null;
      }
    } catch (error) {
      logger.error(
        'Error parsing event data:',
        error,
        'Type:',
        eventType,
        'Data:',
        data
      );
      return null;
    }
  }

  /**
   * Subscribe to message updates
   */
  public subscribe(callback: (messages: ThreadMessage[]) => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Notify all subscribers of message updates
   */
  private notifySubscribers(messages: ThreadMessage[]): void {
    this.subscribers.forEach((callback) => {
      try {
        callback(messages);
      } catch (error) {
        logger.error('Error in stream subscriber:', error);
      }
    });
  }

  /**
   * Get current messages for a thread from state manager
   */
  public getCurrentMessages(threadId: string): ThreadMessage[] {
    return this.messageStateManager.getThreadMessages(threadId);
  }

  /**
   * Clear all per-thread stream state.
   */
  public clear(): void {
    this.startNewMessage();
  }

  /**
   * Check if a message contains a tool call with the given ID
   */
  private messageContainsToolCall(
    message: ThreadMessage,
    toolCallId: string
  ): boolean {
    // Check main content
    for (let i = 0; i < message.content.length; i++) {
      const content = message.content[i];

      if (content.type === 'tool-call') {
        const toolCall = content as ToolCallContent;
        if (toolCall.toolCallId === toolCallId) {
          return true;
        }
      }

      // Check sub-sessions
      if (content.type === 'sub-session') {
        const subSession = content as SubSessionContent;
        if (subSession.content) {
          for (let j = 0; j < subSession.content.length; j++) {
            const subContent = subSession.content[j];
            if (subContent.type === 'tool-call') {
              const subToolCall = subContent as ToolCallContent;
              if (subToolCall.toolCallId === toolCallId) {
                return true;
              }
            }
          }
        }
      }
    }
    logger.debug(
      `[TOOL_SEARCH] No match found for ${toolCallId} in message ${message.id}`
    );
    return false;
  }

  /**
   * Reset stream state for a new conversation turn.
   * When threadId is provided, resets only that thread's state.
   * When omitted, resets all per-thread state.
   */
  public startNewMessage(threadId?: string): void {
    if (threadId !== undefined) {
      this.threadState.delete(threadId);
    } else {
      this.threadState.clear();
    }
  }

  /**
   * Process a complete response stream
   */
  public async processStream(
    response: Response,
    threadId: string,
    exchangeEncryptionKeyValue: string | null,
    onProgress?: (messages: ThreadMessage[]) => void,
    timestamp?: number,
    encryptor?: EncryptorType
  ): Promise<ThreadMessage[]> {
    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    // let's extend  TextDecoder and add a method to decrypt the chunk

    const decoder = new TextDecoder();
    let responseEncryptionKey: AxiosResponse<EncryptionKey> | null = null;
    if (exchangeEncryptionKeyValue) {
      responseEncryptionKey = await exchangeEncryptionKey(
        exchangeEncryptionKeyValue
      );
    }

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        let chunk = decoder.decode(value, { stream: true });
        if (responseEncryptionKey && encryptor) {
          try {
            const chunks = chunk.split('\n\n');
            for (let i = 0; i < chunks.length; i++) {
              const currentChunk = chunks[i];
              const decryptedData = await decryptResponsePayload(
                currentChunk,
                responseEncryptionKey.data.key,
                encryptor
              );
              chunk += decryptedData;
            }
          } catch (error) {
            logger.error(
              'Failed to decrypt stream chunk:',
              error
            );
            throw new Error(`Failed to decrypt response: ${error}`);
          }
        }

        this.processStreamChunk(chunk, threadId, timestamp);

        if (onProgress) {
          onProgress(this.getCurrentMessages(threadId));
        }
      }
    } finally {
      reader.releaseLock();
    }

    return this.getCurrentMessages(threadId);
  }
}
